// ===========================================================================
// frontend/src/screens/CropListing/MyCrops.jsx
// NEW UI — "My Crops" listing page
// Reference: Figma design — 3-column crop card grid
// Uses: NewHeaderNavWithSidebar, Tabler Icons, existing color/font theme system
// Dummy data only — no backend/database changes
// ===========================================================================

import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Chip,
    Menu,
    MenuItem,
    IconButton,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    IconPlus,
    IconSearch,
    IconAdjustmentsHorizontal,
    IconMapPin,
    IconClock,
    IconUsers,
    IconDotsVertical,
    IconStar,
    IconLeaf,
    IconEdit,
    IconTrash,
    IconEye,
    IconShare2,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { colors, fonts, size } from '../../ColorFont/color';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';
import CropDetailsModal from '../../components/CropDetailsModal';
import UnifiedCropCard from '../../components/UnifiedCropCard';
import { url } from '../../ConstUrl/Url';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import FilterDialog from '../../components/common/FilterDialog';
import { SkeletonListing } from '../../components/common/Skeletons';
import BlockedView from '../../components/common/BlockedView';
import { IconShieldLock } from '@tabler/icons-react';

// ─── Constants ───────────────────────────────────────────────────────────────
// PRIMARY and PRIMARY_DARK are now used directly from colors object to ensure reactivity

// ─── Main Component ───────────────────────────────────────────────────────────
const MyCrops = (props) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const userData = props.userData || JSON.parse(localStorage.getItem('userData'));

    // ─── State ──────────────────────────────────────────────────────────────────
    const [crops, setCrops] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('Latest First');
    const [filterAnchor, setFilterAnchor] = useState(null);
    const filterOpen = Boolean(filterAnchor);

    const [selectedCrop, setSelectedCrop] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    
    // Filter states
    const [openFilter, setOpenFilter] = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubCategory, setSelectedSubCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [state, setState] = useState('');
    const [tehsil, setTehsil] = useState('');
    const [stateid, setstateid] = useState(0);

    // ─── Handlers ───────────────────────────────────────────────────────────────
    const handleFilterOpen = () => setOpenFilter(true);
    const handleFilterClose = () => setOpenFilter(false);

    const handleClearFilter = () => {
        setSortBy('Latest First');
        setFilterStatus('');
        setSelectedCategory('');
        setSelectedSubCategory('');
        setState('');
        setTehsil('');
        setstateid(0);
    };

    const handleApplyFilter = () => {
        // Results are computed in useMemo based on state
        setOpenFilter(false);
    };

    const fetchUserCrops = async () => {
        if (!userData?._id) {
            setIsLoading(false);
            return;
        }
        try {
            if (crops.length === 0) setIsLoading(true);
            const response = await fetch(`${url}/api/plots?user=${userData._id}`);
            const data = await response.json();
            
            if (data && data.paginatedPlots) {
                const userCrops = data.paginatedPlots.filter(plot => 
                    plot.user === userData._id || plot.createdBy === userData._id
                );
                setCrops(userCrops);
            } else if (Array.isArray(data)) {
                 const userCrops = data.filter(plot => 
                    plot.user === userData._id || plot.createdBy === userData._id
                );
                setCrops(userCrops);
            }
        } catch (error) {
            console.error('Error fetching crops:', error);
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        fetch(`${url}/api/get-categories`)
            .then(res => res.json())
            .then(data => setCategories(data))
            .catch(err => console.error(err));
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            const cat = categories.find(c => c.categoryType === selectedCategory);
            setSubCategories(cat ? cat.subCategory : []);
        } else {
            setSubCategories([]);
        }
    }, [selectedCategory, categories]);

    useEffect(() => {
        // Prevent re-fetching if we already have crops or if the user ID hasn't changed
        const currentUserId = userData?._id || userData?.uid;
        if (!currentUserId) {
            setIsLoading(false);
            return;
        }

        const role = (userData?.role || '').toLowerCase();
        if (role === 'seller' || role === 'farmer') {
            fetchUserCrops();
        } else {
            setIsLoading(false);
        }
    }, [userData?._id, userData?.uid, userData?.role]);

    const handleDeleteClick = (id) => {
        setCrops(prev => prev.filter(c => c._id !== id));
    };

    const handleEdit = (crop) => {
        navigate('/add-crop-step-1', { 
            state: { 
                isEdit: true, 
                cropId: crop._id,
                cropData: crop 
            } 
        });
    };

    const handleBoost = (updatedUserData) => {
        // Refresh crops to show the 'Sponsored' badge immediately
        fetchUserCrops();
        
        if (updatedUserData && updatedUserData._id) {
            // Additional logic if needed for global state
        }
    };

    const handleViewDetails = (crop) => {
        setSelectedCrop(crop);
        setIsDetailsModalOpen(true);
    };

    const filteredCrops = useMemo(() => {
        let results = [...crops];

        // 1. Search Filter (Real-time)
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            results = results.filter(
                (c) =>
                    (c.subCategory || '').toLowerCase().includes(q) ||
                    (c.variety || '').toLowerCase().includes(q) ||
                    (Array.isArray(c.category) ? c.category.join(' ') : c.category || '').toLowerCase().includes(q) ||
                    (c.taluka || '').toLowerCase().includes(q) ||
                    (c.state || '').toLowerCase().includes(q) ||
                    (c.district || '').toLowerCase().includes(q)
            );
        }

        // 2. Status Filter
        if (filterStatus) {
            results = results.filter(c => c.status === filterStatus);
        }

        // 3. Category Filter
        if (selectedCategory) {
            results = results.filter(c => 
                (Array.isArray(c.category) ? c.category.includes(selectedCategory) : c.category === selectedCategory)
            );
        }
        if (selectedSubCategory) {
            results = results.filter(c => c.subCategory === selectedSubCategory);
        }

        // 4. Location Filter
        if (state) {
            results = results.filter(c => c.state === state);
        }
        if (tehsil) {
            results = results.filter(c => c.taluka === tehsil);
        }

        // 5. Sorting (Real-time)
        switch (sortBy) {
            case 'Price: Low to High':
                results.sort((a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0));
                break;
            case 'Price: High to Low':
                results.sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0));
                break;
            case 'Stock: High to Low':
                // Note: using 'area' as stock field based on UnifiedCropCard.jsx mapping
                results.sort((a, b) => (Number(b.area) || 0) - (Number(a.area) || 0));
                break;
            case 'Latest First':
            default:
                results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                break;
        }

        return results;
    }, [searchQuery, crops, sortBy, filterStatus, selectedCategory, selectedSubCategory, state, tehsil]);

    if (isLoading) {
        return <SkeletonListing />;
    }

    if (!userData || (userData.role !== 'Seller' && userData.role !== 'seller')) {
        return (
            <BlockedView 
                icon={<IconShieldLock size={48} stroke={1.5} color="#FF9433" />}
                title={i18n.t('farmerOnlyTitle')}
                description={i18n.t('farmerOnlyDesc')}
                buttonText={i18n.t('goToHomeBtn')}
                onAction={() => navigate('/home')}
            />
        );
    }

    return (
        <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            // Local padding removed — handled by NewHeaderNavWithSidebar layout
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: { xs: 3, md: 4 },
                flexWrap: 'wrap',
                gap: 2,
            }}>
                <Typography sx={{
                    fontFamily: fonts.poppins,
                    fontWeight: 800,
                    fontSize: { xs: '24px', md: '32px' },
                    color: '#1C252E',
                }}>
                    {i18n.t('dashboard:myListings')}
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<IconPlus size={20} stroke={2.5} />}
                    onClick={() => navigate('/add-crop-step-1')}
                    sx={{
                        backgroundColor: colors.primary,
                        color: '#FFFFFF',
                        borderRadius: '16px',
                        textTransform: 'none',
                        fontFamily: fonts.poppins,
                        fontWeight: 700,
                        fontSize: '15px',
                        px: 3,
                        py: 1.5,
                        boxShadow: `0px 8px 16px ${colors.primary_rgba_24}`,
                        '&:hover': {
                            backgroundColor: colors.primary_dark,
                            boxShadow: `0px 12px 24px ${colors.primary_rgba_32}`,
                        },
                    }}
                >
                    {i18n.t('addNewCropBtn')}
                </Button>
            </Box>

            {/* Search & Filter */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: { xs: 3, md: 4 },
            }}>
                <TextField
                    fullWidth
                    placeholder={i18n.t('searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconSearch size={20} color="#919EAB" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '16px',
                            backgroundColor: '#FFFFFF',
                            fontFamily: fonts.poppins,
                            fontSize: '15px',
                            height: '56px',
                            '& fieldset': { borderColor: '#E0E5EB', borderWidth: '1.5px' },
                            '&:hover fieldset': { borderColor: '#919EAB' },
                            '&.Mui-focused fieldset': { borderColor: colors.primary },
                        },
                    }}
                />

                <FilterDialog
                    open={openFilter}
                    onClose={handleFilterClose}
                    onClear={handleClearFilter}
                    onApply={handleApplyFilter}
                    state={state}
                    setState={setState}
                    tehsil={tehsil}
                    setTehsil={setTehsil}
                    stateid={stateid}
                    setstateid={setstateid}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    selectedSubCategory={selectedSubCategory}
                    setSelectedSubCategory={setSelectedSubCategory}
                    categories={categories}
                    subCategories={subCategories}
                    filterStatus={filterStatus}
                    setFilterStatus={setFilterStatus}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    showOwnPlots={false} // Already in My Crops
                />
            </Box>

            {/* Grid */}
            <Typography sx={{
                fontFamily: fonts.poppins,
                fontSize: '15px',
                fontWeight: 600,
                color: '#637381',
                mb: 3,
            }}>
                {filteredCrops.length} {filteredCrops.length === 1 ? i18n.t('cropFound') : i18n.t('cropsFound')}
            </Typography>

            {filteredCrops.length > 0 ? (
                <Grid container spacing={{ xs: 2, md: 4 }}>
                    {filteredCrops.map((crop) => (
                        <Grid item xs={12} sm={6} md={4} key={crop._id} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <UnifiedCropCard 
                                crop={crop} 
                                userData={userData}
                                onDeleteSuccess={handleDeleteClick}
                                onEdit={handleEdit}
                                onBoost={handleBoost}
                                onDetails={handleViewDetails}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 12,
                    gap: 2,
                    backgroundColor: '#FFFFFF',
                    borderRadius: '24px',
                    border: '2px dashed #E0E5EB',
                }}>
                    <Box sx={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '30px',
                        backgroundColor: colors.primary_rgba_12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                    }}>
                        <IconLeaf size={48} color={colors.primary} />
                    </Box>
                    <Typography variant="h5" sx={{ fontFamily: fonts.poppins, fontWeight: 800, color: '#1C252E' }}>
                        {i18n.t('startSellingToday')}
                    </Typography>
                    <Typography sx={{
                        fontFamily: fonts.poppins,
                        fontSize: '16px',
                        color: '#637381',
                        textAlign: 'center',
                        maxWidth: '400px',
                        mb: 3,
                    }}>
                        {i18n.t('noCropsDesc')}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<IconPlus size={22} />}
                        onClick={() => navigate('/add-crop-step-1')}
                        sx={{
                            backgroundColor: colors.primary,
                            color: '#FFFFFF',
                            borderRadius: '16px',
                            textTransform: 'none',
                            fontFamily: fonts.poppins,
                            fontWeight: 700,
                            fontSize: '16px',
                            px: 5,
                            py: 1.8,
                            boxShadow: `0px 8px 16px ${colors.primary_rgba_24}`,
                        }}
                    >
                        {i18n.t('listFirstCropBtn')}
                    </Button>
                </Box>
            )}

            <CropDetailsModal
                open={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                crop={selectedCrop}
                userData={userData}
                onGetContact={() => {}}
            />
        </Box>
    );
};

export default MyCrops;
